import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useState } from "react";
import { useForm } from "react-hook-form";

import loginCore from "../../core/login.core";
import { useTranslation } from "../../core/translation.core";
import userSessionService from "../../service/userSessionService";
import { getDomain } from "../../core/cookie.core";
import EyeIcon from "../../icons/eye.icon";
import EyeClosedIcon from "../../icons/eyeClosed.icon";
import { LoginErrorMessage } from "../../types/login";
import FormAlert from "../forms/alert.comp";
import EmailOrTelInput from "./emailOrTelInput.comp";

export interface CombinedLoginType {
  countryCode: string;
  mobileNumber: string;
  userName: string;
  password: string;
  email: string;
}

interface LoginResponse {
  status: number;
  data?: {
    userId?: string;
    userName?: string;
    token?: string;
    message?: LoginErrorMessage;
    [key: string]: any;
  };
}

export default function CombinedLogin() {
  const { push, query } = useRouter();
  const { t, msg } = useTranslation();

  const [inputType, setInputType] = useState<"text" | "password">("password");
  const handleShow = () => setInputType((prev) => (prev === "text" ? "password" : "text"));

  const methods = useForm<CombinedLoginType>();

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleAlertReset = () => {
    setErrorMessage("");
    setSuccessMessage("");
  };

  const inputClassName =
    "h-9 w-full bg-transparent text-[#474747] border-b-[1px] border-[#474747] outline-none placeholder:text-[#474747] px-2";

  async function onSubmit(data: CombinedLoginType) {
    setIsLoading(true);
    handleAlertReset();

    try {
      // Type assertion to tell TypeScript what structure to expect
      const res = (!data.email ? await loginCore.loginByMobileNumber(data) : await loginCore.loginByEmail(data)) as LoginResponse;

      if (res.status === 200) {
        // Successful login
        // Set session with the returned user data
        userSessionService.setSession({
          userId: res.data?.userId || "user-id",
          userName: res.data?.userName || (data.email ? data.email.split("@")[0] : data.mobileNumber),
          email: data.email || "",
          token: res.data?.token || "",
        });

        // Handle redirect after login
        if (query.redirectTo && typeof query.redirectTo === "string") {
          // Redirect to the specified page
          push(query.redirectTo);
        } else if (query.checkingOut) {
          // For backward compatibility, redirect to checkout if checkingOut is true
          push("/cart");
        } else {
          // Default redirect
          push("/");
        }

        return;
      }

      // Rest of your error handling logic remains unchanged
      if (res.status === 401) {
        // Make sure data exists before trying to access properties
        if (!res.data) {
          setErrorMessage(t(msg.general.errSubmit1));
          return;
        }

        const errorMessage = res.data.message as LoginErrorMessage;
        console.log("Login error message:", errorMessage);

        switch (errorMessage) {
          case "emailNeedVerify":
            // Store the token without the "local:" prefix - this was causing issues
            if (res.data.token) {
              console.log("Setting temp token for verification");
              Cookies.set("temp-token", res.data.token, {
                domain: getDomain(),
                expires: 1, // Expires in 1 day
                sameSite: "strict",
              });

              // Also store userId if available for verification
              if (res.data.userId) {
                Cookies.set("userId", res.data.userId, {
                  domain: getDomain(),
                  expires: 1,
                  sameSite: "strict",
                });
              }

              // Redirect to verification page
              push("/auth/verifyEmail");
            } else {
              console.error("No token provided with emailNeedVerify response");
              setErrorMessage(t(msg.login.errorMessage));
            }
            break;

          case "mobileNeedVerify":
            // Backend blocks mobile login until the SMS OTP is verified; route to the verify page
            if (res.data.token) {
              Cookies.set("temp-token", res.data.token, {
                domain: getDomain(),
                expires: 1,
                sameSite: "strict",
              });

              if (res.data.userId) {
                Cookies.set("userId", res.data.userId, {
                  domain: getDomain(),
                  expires: 1,
                  sameSite: "strict",
                });
              }

              push({
                pathname: "/auth/verifyMobileOtp",
                query: {
                  userId: res.data.userId,
                  mobileLast4: res.data.mobileLast4,
                  mode: "verifyOtp",
                },
              });
            } else {
              setErrorMessage(t(msg.login.errorMessage));
            }
            break;

          case "legacyNotAllowedForMobileLogin":
            setErrorMessage(t(msg.login.legacyMobileNotAllowed));
            break;

          case "noRecord":
          case "Unable to find user":
            setErrorMessage(t(msg.login.noCredentials));
            break;

          case "Wrong password":
            setErrorMessage(t(msg.login.errorMessage));
            break;

          default:
            setErrorMessage(t(msg.general.errSubmit1));
        }
      } else {
        // Handle other error statuses
        setErrorMessage(t(msg.general.errSubmit1));
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage(t(msg.general.errSubmit1));
    } finally {
      setIsLoading(false);
    }
  }
  return (
    <>
      <FormAlert successMessage={successMessage} errorMessage={errorMessage} handleReset={handleAlertReset} />

      <form className="flex flex-col" onSubmit={methods.handleSubmit(onSubmit)}>
        <>
          {Object.values(methods.formState.errors).map((m, idx) => (
            <p className="w-full text-red-400 text-xs mb-2" key={idx}>
              {m.message}
            </p>
          ))}
        </>

        <EmailOrTelInput methods={methods as any} />

        <div className="flex mb-6">
          <label className={`h-9 flex items-center text-[#474747] border-[#474747] w-20`}>{t(msg.login.password)}:</label>

          <div className="relative w-full">
            <input
              className={`${inputClassName}`}
              type={inputType}
              placeholder={t(msg.login.password)}
              {...methods.register("password", { required: t(msg.login.passwordR) })}
            />
            <span onClick={handleShow} className="absolute top-1/2 -translate-y-1/2 right-2 cursor-pointer text-gray-500">
              {inputType === "password" ? <EyeClosedIcon className="w-6 h-6" /> : <EyeIcon className="w-6 h-6" />}
            </span>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 font-medium text-white bg-primary bg-primary-dark transition-colors rounded-md"
        >
          {isLoading ? t(msg.common.loading) : t(msg.login.login)}
        </button>
      </form>
    </>
  );
}
